import { Link } from "react-router-dom";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ToolCard } from "../components/ToolCard.jsx";
import { FolderGrid } from "../components/FolderGrid.jsx";
import BackArrow from "../components/BackArrow.jsx";
import { BioShell, BIO_ACCENT, VIOLET } from "./BioShell.jsx";

// V45.3 — BioHome is now a custom ChemHome-style layout (not a BioShell wrapper).
const EM = BIO_ACCENT;      // green #34C759
const EM_DEEP = VIOLET;
const EM_LIGHT = "#A7F3D0"; // light green for gradient-text

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// BioLanding — preserved verbatim from pre-V45.3.
export function BioLanding() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", color: "#E8E9F3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div style={{ width: 72, height: 72, borderRadius: 18, background: `linear-gradient(135deg, ${BIO_ACCENT}, ${VIOLET})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700, color: "#fff", marginBottom: 20 }}>
        Bi
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: "0 0 8px" }}>NerdBio</h1>
      <p style={{ fontSize: 15, color: "#9A9DBE", margin: "0 0 28px", maxWidth: 340 }}>
        Genetics and molecular biology tools, plus an anatomy reference. For students. Free. Fast. Actually good.
      </p>
      <Link to="/bio/home" style={{ background: BIO_ACCENT, color: "#04240F", fontWeight: 600, fontSize: 15, padding: "13px 28px", borderRadius: 12, textDecoration: "none" }}>
        Open NerdBio
      </Link>
    </div>
  );
}

export function BioHome() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: "70%", background: `radial-gradient(ellipse at center bottom, ${rgba(EM, 0.18)} 0%, transparent 70%)`, pointerEvents: "none" }} />

      <header style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px",
        paddingTop: "calc(16px + env(safe-area-inset-top))",
        paddingLeft: "calc(24px + env(safe-area-inset-left))",
        paddingRight: "calc(24px + env(safe-area-inset-right))",
        position: "relative", zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BackArrow to="/" from={EM} toColor={EM_LIGHT} />
          <div style={{ width: 38, height: 38, borderRadius: 11, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, color: "#fff" }}>Bi</div>
          <svg width="100" height="24" viewBox="0 0 100 24" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
            <defs>
              <linearGradient id="wm-bio" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={EM_LIGHT} />
                <stop offset="1" stopColor="#6EE6A0" />
              </linearGradient>
            </defs>
            <text x="0" y="19" fill="url(#wm-bio)" fontFamily="DM Sans, system-ui, sans-serif" fontSize="20" fontWeight="800" letterSpacing="-0.5">NerdBio</text>
          </svg>
        </div>
      </header>

      <main style={{ maxWidth: 820, margin: "0 auto", padding: "40px 20px 80px", position: "relative", zIndex: 10 }}>
        <h1 style={{ fontSize: "clamp(32px, 6vw, 52px)", fontWeight: 800, letterSpacing: "-1.5px", textAlign: "center", margin: "0 0 14px", lineHeight: 1.1 }}>
          Biology tools.<br /><span style={{ background: `linear-gradient(135deg, ${EM_LIGHT}, ${EM})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Free. Fast. Actually good.</span>
        </h1>
        <p style={{ fontSize: 17, color: "#9CA3AF", textAlign: "center", maxWidth: 580, margin: "0 auto 14px", lineHeight: 1.6 }}>
          Biology as systems, not vocabulary. Anatomy reference, Punnett squares, DNA translation, Hardy-Weinberg — the structures and patterns under the wall of unfamiliar terms.
        </p>
        <p style={{ fontSize: 15, color: "#6F7290", textAlign: "center", maxWidth: 580, margin: "0 auto 44px", lineHeight: 1.6 }}>
          Free. No signup. No dark patterns.
        </p>

        <FolderGrid ariaLabel="Biology tools">
          <ToolCard accent={EM} to="/bio/dna" name="DNA / RNA Toolkit" desc="Transcribe, complement, reverse-complement, GC content" />
          <ToolCard accent={EM} to="/bio/translate" name="Codon Translator" desc="DNA → protein using the standard genetic code" />
          <ToolCard accent={EM} to="/bio/punnett" name="Punnett Square" desc="Mono- and dihybrid crosses with phenotype ratios" />
          <ToolCard accent={EM} to="/bio/hardy-weinberg" name="Hardy-Weinberg" desc="Allele and genotype frequencies at equilibrium" />
          <ToolCard accent={EM} to="/bio/lab" name="Lab Solutions" desc="Molarity and C₁V₁ = C₂V₂ dilution" />
          <ToolCard accent={EM} to="/bio/anatomy" name="Anatomy Reference" desc="Systems, nerves, bones, muscles & more — with quiz" />
          <ToolCard accent={EM} to="/bio/reference" name="Biology Reference" desc="Cells, genetics, evolution, ecology & more — with quiz" />
        </FolderGrid>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="bio" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
